import { computed, ref } from "vue";

export class Steps {
    constructor(steps = {}, defaults = {}) {
        Object.assign(
            this,
            Object.fromEntries(
                Object.entries(steps).map(([key, step]) => [
                    key,
                    new Step({ ...defaults, ...step }),
                ])
            )
        );
        this[Object.keys(this)[0]].active = true;
    }

    active() {
        const keys = Object.keys(this);
        const currentIndex = keys.indexOf(
            Object.keys(this).find((key) => this[key].active)
        );
        const done = currentIndex >= Object.keys(this).length - 1;
        return {
            key: keys[currentIndex],
            step: this[currentIndex],
            done,
        };
    }

    next() {
        const keys = Object.keys(this);
        const currentIndex = keys.indexOf(
            Object.keys(this).find((key) => this[key].active)
        );
        if (currentIndex + 1 >= keys.length) {
            return {
                key: keys[currentIndex],
                step: this[currentIndex],
                done: true,
            };
        }
        const nextStep = keys[currentIndex + 1];
        this[keys[currentIndex]].active = false;
        this[nextStep].active = true;
        return { key: nextStep, step: this[nextStep], done: false };
    }

    previous() {
        const keys = Object.keys(this);
        const currentIndex = keys.indexOf(
            Object.keys(this).find((key) => this[key].active)
        );
        if (currentIndex - 1 < 0) {
            return {
                key: keys[currentIndex],
                step: this[currentIndex],
                done: true,
            };
        }
        const previousStep = keys[currentIndex - 1];
        this[keys[currentIndex]].active = false;
        this[previousStep].active = true;
        return { key: previousStep, step: this[previousStep], done: false };
    }

    goTo(key) {
        if (!this[key]) {
            return { key: key, step: null, done: false };
        }
        Object.keys(this).forEach((stepKey) => {
            this[stepKey].active = stepKey === key;
        });
        const done =
            Object.keys(this).indexOf(key) >= Object.keys(this).length - 1;
        return { key: key, step: this[key], done };
    }

    reset() {
        Object.keys(this).forEach((stepKey) => {
            this[stepKey].active = false;
        });
        this[Object.keys(this)[0]].active = true;
    }
}

export class Step {
    constructor({
        variant = undefined,
        background = undefined,
        title = "",
        description = "",
        crumb = {},
        withSearch = false,
        showClear = false,
        search = undefined,
        searchProps = {},
        header = undefined,
        headerProps = {},
        content = undefined,
        props = {},
        footer = undefined,
        footerProps = {},
        active = false,
        validate = () => true,
    } = {}) {
        this.variant = variant;
        this.background = background;
        this.title = title;
        this.description = description;
        this.crumb = {
            name: "",
            navigation: true,
            ...crumb,
        };
        this.withSearch = withSearch;
        this.showClear = showClear;
        this.search = search;
        this.searchProps = searchProps;
        this.header = header;
        this.headerProps = headerProps;
        this.content = content;
        this.props = props;
        this.footer = footer;
        this.footerProps = footerProps;
        this.active = active;
        this.validate = validate;
    }
}

export const useSteps = (steps) => {
    const forward = ref(true);
    const breadcrumbs = ref([]);

    const navigationState = computed(() =>
        breadcrumbs.value.map((crumb) => crumb.id)
    );

    const currentStep = computed(() => navigationState.value.slice(-1)[0]);

    function handleGoToStep(key) {
        const index = navigationState.value.indexOf(key);
        if (index !== -1) {
            forward.value = false;
            breadcrumbs.value = breadcrumbs.value.slice(0, index + 1);
        } else {
            forward.value = true;
        }

        const { step } = steps.goTo(key);

        if (index === -1 && step) {
            breadcrumbs.value.push({
                id: key,
                title: step.crumb.name,
                navigation: step.crumb.navigation,
            });
        }
    }

    function handleStepForward() {
        if (!steps[currentStep.value].validate()) {
            return;
        }

        forward.value = true;
        const { key, step, done } = steps.next();
        if (done) {
            return;
        }

        breadcrumbs.value.push({
            id: key,
            title: step.crumb.name,
            navigation: step.crumb.navigation,
        });
    }

    function handleStepBack() {
        forward.value = false;
        const { done } = steps.previous();
        if (!done) {
            breadcrumbs.value.pop();
        }
    }

    function reset() {
        steps.reset();
        const [key, step] = Object.entries(steps)[0];
        breadcrumbs.value = [
            {
                id: key,
                title: step.crumb.name,
                navigation: step.crumb.navigation,
            },
        ];
    }

    return {
        forward,
        breadcrumbs,
        navigationState,
        currentStep,
        handleGoToStep,
        handleStepForward,
        handleStepBack,
        reset,
    };
};
